/**
 * A client-swappable colour palette - port of the Unity `ThemePalette`. One
 * palette re-skins every panel; markup never changes.
 *
 * Only a small set of core tokens are stored; the many translucent surface,
 * border and state tints used across the UI are derived from these at runtime
 * (see the helpers below) so a new theme only needs to set ~12 colours.
 *
 * Colours are `#rrggbb` strings; every derived token is an `#rrggbbaa` string,
 * because that is the one form both the uikitml stylesheet dialect and
 * uikit's runtime `setProperties` accept for alpha (there is no
 * `background-opacity` any more).
 */
export interface ThemePalette {
  readonly name: string;
  readonly displayName: string;
  /** True for light backgrounds - controls whether translucent overlays are light-on-dark or dark-on-light. */
  readonly isLight: boolean;
  /** Scene / canvas backdrop colour. */
  readonly root: string;
  /** Opaque plate (panel) colour, before plate alpha is applied. */
  readonly plateColor: string;
  readonly plateAlpha: number;
  /** RGB tint used for every translucent overlay/border. White on dark themes, near-black navy on light. */
  readonly overlayTint: string;
  readonly accent: string;
  readonly accentHover: string;
  /** Text / icon colour drawn on top of a solid accent fill. */
  readonly onAccent: string;
  readonly textHi: string;
  readonly textMid: string;
  readonly textLo: string;
  /** Label colour for secondary / outline buttons. */
  readonly secondaryText: string;
  readonly danger: string;
}

/** Parse `#rgb` / `#rrggbb` / `#rrggbbaa` into components (0-255, alpha 0-1). */
export function parseHex(hex: string): { r: number; g: number; b: number; a: number } {
  let value = hex.trim().replace(/^#/, '');
  if (value.length === 3 || value.length === 4) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(value)) return { r: 255, g: 0, b: 255, a: 1 }; // magenta, like the Unity helper
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const a = value.length === 8 ? parseInt(value.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
}

function toHex2(value: number): string {
  return Math.round(Math.min(255, Math.max(0, value)))
    .toString(16)
    .padStart(2, '0');
}

/** `#rrggbbaa` from a colour and an alpha (0-1). */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}${toHex2(alpha * 255)}`;
}

/** Linear blend of two colours (alpha ignored, result opaque). */
export function lerpColor(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  const k = Math.min(1, Math.max(0, t));
  return `#${toHex2(a.r + (b.r - a.r) * k)}${toHex2(a.g + (b.g - a.g) * k)}${toHex2(a.b + (b.b - a.b) * k)}`;
}

// ---- Derived tokens (computed from the core set, mirroring ThemePalette.cs) ----

/** Panel fill including the configured plate alpha. */
export function panel(t: ThemePalette): string {
  return withAlpha(t.plateColor, t.plateAlpha);
}

/** A translucent overlay of the theme tint at the given alpha. */
export function overlay(t: ThemePalette, alpha: number): string {
  return withAlpha(t.overlayTint, alpha);
}

/** The accent colour at the given alpha. */
export function accentA(t: ThemePalette, alpha: number): string {
  return withAlpha(t.accent, alpha);
}

export const panelBorder = (t: ThemePalette): string => overlay(t, 0.14);
export const secondaryBg = (t: ThemePalette): string => overlay(t, 0.06);
export const secondaryBorder = (t: ThemePalette): string => overlay(t, 0.16);

export const stepCurrentBg = (t: ThemePalette): string => accentA(t, 0.12);
export const stepDoneDotBg = (t: ThemePalette): string => accentA(t, 0.18);
export const stepTodoBg = (t: ThemePalette): string => overlay(t, 0.03);
export const stepTodoBorder = (t: ThemePalette): string => overlay(t, 0.07);
export const stepTodoDotBg = (t: ThemePalette): string => overlay(t, 0.08);

export const hintBg = (t: ThemePalette): string => accentA(t, 0.08);
export const hintBorder = (t: ThemePalette): string => accentA(t, 0.35);

export const annotationText = (t: ThemePalette): string => withAlpha(t.textLo, 0.85);
export const annotationRule = (t: ThemePalette): string => overlay(t, 0.2);

export const dangerBg = (t: ThemePalette): string => withAlpha(t.danger, 0.1);
export const dangerBorder = (t: ThemePalette): string => withAlpha(t.danger, 0.35);
export const dangerText = (t: ThemePalette): string => lerpColor(t.danger, '#ffffff', t.isLight ? 0 : 0.45);
